// Locale-aware date/time/number formatting. Each helper takes the caller's
// active locale explicitly (from useLocale(), which reads the [lang] URL
// segment) rather than reading a module-level i18n instance: the app renders
// three separate locale-scoped i18next instances (see getInstanceForLocale in
// ./index), so there is no single "current" global language to read here.

import { SITE } from '@site';

import { intlLocaleTag, type Locale } from './locales';

// Everything here renders twice: into the static HTML at export time (Node,
// in UTC, with Node's ICU/CLDR data) and again in the visitor's browser during
// hydration (their timezone, their engine's CLDR). Any difference is a React
// hydration error (#418) and a full client re-render of the page. So every
// formatter names a timezone, and where CLDR versions disagree on the pattern
// itself the string is assembled from formatToParts() in a fixed order.

// The site's timezone — the same value as APP_TZ in ../date-range.ts,
// which imports this module (so it can't be imported from there).
const SITE_TZ = SITE.timeZone;

type LongDateParts = { weekday: string; day: string; month: string; year: string };

// The layout of the long date per locale. CLDR versions disagree on it — e.g.
// en-GB is "Thursday, 15 October 2026" in Node 22 (CLDR 47) but "Thursday 15
// October 2026" in current Chromium — so the words come from Intl and the
// order and punctuation from here.
// Keyed by language, or by the full tag where a site's variety differs (en-US:
// "Thursday, October 15, 2026"). Languages without an entry use English's.
const LONG_DATE_LAYOUT: Record<string, (p: LongDateParts) => string> = {
  en: (p) => `${p.weekday} ${p.day} ${p.month} ${p.year}`,
  'en-US': (p) => `${p.weekday}, ${p.month} ${p.day}, ${p.year}`,
  fr: (p) => `${p.weekday} ${p.day} ${p.month} ${p.year}`,
  de: (p) => `${p.weekday}, ${p.day}. ${p.month} ${p.year}`,
  es: (p) => `${p.weekday}, ${p.day} de ${p.month} de ${p.year}`,
  ru: (p) => `${p.weekday}, ${p.day} ${p.month} ${p.year} г.`,
};

/** Weekday, day, month, year — e.g. event detail header. */
export function formatDateLong(iso: string, tz: string, locale: Locale): string {
  const parts = new Intl.DateTimeFormat(intlLocaleTag(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: tz,
  }).formatToParts(new Date(iso));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return (LONG_DATE_LAYOUT[intlLocaleTag(locale)] ?? LONG_DATE_LAYOUT[locale] ?? LONG_DATE_LAYOUT.en)({
    weekday: part('weekday'),
    day: part('day'),
    month: part('month'),
    year: part('year'),
  });
}

/** Long calendar date without weekday, in the site's timezone — e.g. article detail. */
export function formatDateMedium(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: SITE_TZ,
  }).format(new Date(iso));
}

/**
 * Day + short month + year in the event's own timezone — e.g. "24 Jan 2027".
 * Used to qualify `<title>` on a one-off show's page (a recurring show's title
 * names no single date) and in the city events list.
 */
export function formatDateCompact(iso: string, tz: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: tz,
  }).format(new Date(iso));
}

/** Short numeric date, in the site's timezone — e.g. article card. */
export function formatDateShort(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), { timeZone: SITE_TZ }).format(new Date(iso));
}

/** Day + short month in the app timezone — e.g. the date-filter chips/phrase. */
export function formatDayMonth(date: Date, tz: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), {
    day: 'numeric',
    month: 'short',
    timeZone: tz,
  }).format(date);
}

/** Short weekday label for the day picker. */
export function formatWeekday(date: Date, tz: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), { weekday: 'short', timeZone: tz }).format(
    date,
  );
}

/** Day-of-month number for the day picker. */
export function formatDayNumber(date: Date, tz: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleTag(locale), { day: 'numeric', timeZone: tz }).format(
    date,
  );
}

/** 24h start[–end] time range in the given timezone. */
export function formatTimeRange(
  startIso: string,
  endIso: string | null,
  tz: string,
  locale: Locale,
): string {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
  };
  const fmt = new Intl.DateTimeFormat(intlLocaleTag(locale), opts);
  const start = fmt.format(new Date(startIso));
  if (!endIso) return start;
  return `${start} – ${fmt.format(new Date(endIso))}`;
}

// Symbols written before the amount ("€120", "$120" on miami); any other code is shown as is.
const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$' };

/** Prefix for a price in `currency` — "€" for EUR, "$" for USD, else the code itself ('' when unknown). */
export function currencySymbol(currency: string | null | undefined): string {
  return currency ? (CURRENCY_SYMBOLS[currency] ?? currency) : '';
}
